import { useState } from 'react';
import { BellOff, BellRing } from 'lucide-react';
import { Button, Card, CardMeta, CardTitle, StatusBadge } from '@/design/components';
import { useMe } from '@/auth/useMe';
import { pushState, subscribeToPush, unsubscribeFromPush } from './push';

/**
 * הפעלת התראות על המכשיר הזה (מסמך התכנון 4.5).
 * ההרשאה נדרשת רק אחרי הסבר קצר, ולא בעלייה הראשונה של האפליקציה.
 */
export function PushCard() {
  const { me } = useMe();
  const [state, setState] = useState(() => pushState());
  const [message, setMessage] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  if (!me) return null;

  async function enable() {
    setMessage(null);
    setIsBusy(true);
    const result = await subscribeToPush(me!.employeeId);
    setIsBusy(false);
    setState(pushState());
    setMessage(result.ok ? 'ההתראות הופעלו במכשיר הזה.' : result.message);
  }

  async function disable() {
    setIsBusy(true);
    await unsubscribeFromPush();
    setIsBusy(false);
    setState(pushState());
    setMessage('ההתראות כובו במכשיר הזה. הן ימשיכו להופיע במרכז ההתראות.');
  }

  return (
    <Card>
      <CardTitle>התראות במכשיר הזה</CardTitle>

      {state === 'granted' ? (
        <>
          <StatusBadge tone="good">פעיל</StatusBadge>
          <CardMeta>
            ההתראות שסימנת בהגדרות יגיעו לטלפון, בכפוף לשעות השקט.
          </CardMeta>
          <div>
            <Button variant="secondary" disabled={isBusy} onClick={() => void disable()}>
              <BellOff size={20} strokeWidth={1.75} aria-hidden="true" />
              כיבוי במכשיר הזה
            </Button>
          </div>
        </>
      ) : (
        <>
          <CardMeta>
            {state === 'needs-install'
              ? 'באייפון צריך קודם להתקין את האפליקציה למסך הבית, ואז אפשר להפעיל התראות.'
              : state === 'denied'
                ? 'ההתראות חסומות בהגדרות הדפדפן. צריך להפעיל אותן שם ואז לחזור לכאן.'
                : state === 'unsupported'
                  ? 'הדפדפן הזה לא תומך בהתראות.'
                  : 'בלי התראות צריך לפתוח את האפליקציה כדי לדעת שיש הודעה חדשה או סידור.'}
          </CardMeta>

          {(state === 'default' || state === 'not-configured') && (
            <div>
              <Button
                variant="primary"
                disabled={isBusy || state === 'not-configured'}
                onClick={() => void enable()}
              >
                <BellRing size={20} strokeWidth={1.75} aria-hidden="true" />
                {isBusy ? 'מפעיל' : 'הפעלת התראות'}
              </Button>
            </div>
          )}
        </>
      )}

      {message && (
        <p role="status" className="m-0 text-body-sm text-muted">
          {message}
        </p>
      )}
    </Card>
  );
}
